import {
  ScheduleType,
  Edge,
  PhoneHoursExpansion,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { genUUIDV4 } from '@frontend/string';
import {
  NodeStateWithAdditionalData,
  NodeStateWithAdditionalDataOfType,
} from '../../call-route-details/add-step-panel/types';
import { getRepeatPhoneTreeName } from '../../call-route-details/add-step-panel/utils';
import { logError } from '../utils';
import { getNewNodeAndEdge, sortEdgesDepthFirst } from './graph-utils';
import { getDescendantNodes, isAssociativeNode, isPhoneTreeDescendant, isTerminalNode } from './node-utils';

/**
 * Helper function used to get the updated nodes and edges for a non-associative edit operation.
 * NOTE: An associative node is a node that has downstream dependencies (e.g.phone tree and phone hours nodes)
 * so non-associative nodes are nodes that do not have downstream dependencies.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.processingNodeId - The ID of the node being processed.
 * @param {Node[]} params.nodes - The array of nodes.
 * @param {Edge[]} params.edges - The array of edges.
 * @param {Object} params.data - Additional data for the function.
 * @param {string} params.data.type - The type of the node.
 * @param {NodeStateWithAdditionalData} params.data.callObject - The call object state.
 *
 * @returns {Object} An object containing the updated nodes and edges.
 * @returns {Node[]} returns.nodes - The updated array of nodes.
 * @returns {Edge[]} returns.edges - The updated array of edges.
 */
export const getNodesForNonAssociativeEdit = ({
  processingNodeId,
  nodes,
  edges,
  nodeState,
}: {
  processingNodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  nodeState: NodeStateWithAdditionalData['nodeState'];
}) => {
  const { type, callObject } = nodeState;

  if (!type || !callObject || !CallRouteV1.Types.isCallObject(callObject)) {
    logError('Missing type or callObject: PC-8ekfn33a', 'getNodesForAssociativeEdit', {
      processingNodeId,
    });
    return {
      nodes,
      edges,
    };
  }

  const editingNode = nodes.find((node) => node.id === processingNodeId);
  if (!editingNode) {
    logError('Could not find editing node: PC-jf739ng', 'getNodesForAssociativeEdit', {
      processingNodeId,
      nodeIds: nodes.map((node) => node.id),
    });
    return { nodes, edges };
  }
  const oldTypeIsAssociative = isAssociativeNode(editingNode.type);

  const index = nodes.findIndex((node) => node.id === processingNodeId);
  if (index === -1) {
    logError('Could not find editing node: PC-hdk397g2', 'getNodesForAssociativeEdit', {
      processingNodeId,
      nodeIds: nodes.map((node) => node.id),
    });
    return { nodes, edges };
  }

  let newEdges = [...edges];
  let newNodes = [...nodes];
  const newNode = {
    id: processingNodeId,
    type: type,
    callObject,
    isPhoneTreeDescendent: isPhoneTreeDescendant(processingNodeId ?? '', nodes, edges),
  };

  if (!CallRouteV1.Types.isNodeOfCorrectTypeAndCallObject(newNode)) {
    logError('Invalid node type and call object configuration: PC-64jhfndk2', 'getNodesForAssociativeEdit', {
      processingNodeId,
      type: newNode.type,
      callObjectProperties: Object.keys(newNode.callObject),
    });
    return {
      nodes,
      edges,
    };
  }

  newNodes[index] = newNode;

  const newNodeIsTerminal = isTerminalNode(nodeState);

  // if the old type was an associative node or if the new type is a terminal node, then we need to delete all
  // of the descendant nodes of the node that the user is editing and then add the new node.
  if (oldTypeIsAssociative || newNodeIsTerminal) {
    const descendants = getDescendantNodes({
      nodeId: processingNodeId ?? '',
      nodes: newNodes,
      edges: newEdges,
    });
    const descendantNodeIds = descendants.map((node) => node.id);

    // Filter out all descendant nodes from the nodes array.
    newNodes = newNodes.filter((node) => !descendantNodeIds.includes(node.id));

    // Filter out all edges that connect any descendant nodes of the nodeToDelete and also the edge that connects
    // the parent node to the nodeToDelete.
    newEdges = newEdges.filter(
      (edge) => !descendantNodeIds.includes(edge.sourceId) && !descendantNodeIds.includes(edge.targetId)
    );
  }

  return sortEdgesDepthFirst({ nodes: newNodes, edges: newEdges });
};

/**
 * Helper function used to get the updated nodes and edges for a non-associative add operation in a call graph.
 * NOTE: An associative node is a node that has downstream dependencies (e.g.phone tree and phone hours nodes)
 * so non-associative nodes are nodes that do not have downstream dependencies.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.processingNodeId - The ID of the node being processed.
 * @param {Node[]} params.nodes - The current list of nodes.
 * @param {Edge[]} params.edges - The current list of edges.
 * @param {NodeStateWithAdditionalData} params.data - The data containing the type and call object.
 * @returns {Object} An object containing the updated nodes and edges.
 * @returns {Node[]} returns.nodes - The updated list of nodes.
 * @returns {Edge[]} returns.edges - The updated list of edges.
 */
export const getNodesForNonAssociativeAdd = ({
  processingNodeId,
  nodes,
  edges,
  nodeState,
}: {
  processingNodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  nodeState: NodeStateWithAdditionalData['nodeState'];
}) => {
  const { type, callObject } = nodeState;

  if (!type || !callObject || !CallRouteV1.Types.isCallObject(callObject)) {
    logError('Missing type or callObject: PC-jdnf638', 'getNodesForAssociativeAdd', {
      processingNodeId,
    });
    return {
      nodes,
      edges,
    };
  }

  // Check to make sure that the node that the user wants to add a step to exists.
  const extendingNode = nodes.find((node) => node.id === processingNodeId);
  if (!extendingNode) {
    logError('Could not find extending node: PC-jdh368s', 'getNodesForAssociativeAdd', {
      processingNodeId,
      nodeIds: nodes.map((node) => node.id),
      type,
    });
    return {
      nodes,
      edges,
    };
  }

  // Create the new node with a random id and the type of the node that the user selected.
  const newNodeId = genUUIDV4();
  const newNode = {
    id: newNodeId,
    type,
    callObject,
    isPhoneTreeDescendent: isPhoneTreeDescendant(processingNodeId ?? '', nodes, edges),
  };

  if (!CallRouteV1.Types.isNodeOfCorrectTypeAndCallObject(newNode)) {
    logError('Invalid node type and call object configuration: PC-planft3', 'getNodesForAssociativeAdd', {
      processingNodeId,
      type: newNode.type,
      callObjectProperties: Object.keys(newNode.callObject),
    });
    return {
      nodes,
      edges,
    };
  }

  // Check if the node that the user wants to add a step to is a leaf node. If it is a leaf node, then we need to
  // create a new edge that connects the new node to the parent node.
  // If it is not a leaf node, then we need to create a new edge that connects the new node to the
  // first child node of the parent node and update the parent node's edge's targetId to target the new node.
  const isLeafNode = edges?.every((edge) => edge.sourceId !== processingNodeId);

  if (isLeafNode) {
    // Create the new edge that connects the new node to the parent node.
    const newEdge = {
      id: `${processingNodeId}:${newNodeId}`,
      sourceId: processingNodeId ?? '',
      targetId: newNodeId,
      type: 'basic',
    };

    const newNodes = [...nodes, newNode];
    const newEdges = [...edges, newEdge];

    return sortEdgesDepthFirst({ nodes: newNodes, edges: newEdges });
  } else {
    // Get the edge that is currently connected to the parent node that will be connected to the new node.
    const parentEdge = edges?.find((edge) => edge.sourceId === processingNodeId);
    if (!parentEdge) {
      logError('Could not find edge with sourceId of extending node: PC-e84jl20', 'getNodesForAssociativeAdd', {
        processingNodeId,
        type: newNode.type,
        edgesSourceIds: edges.map((edge) => edge.sourceId),
      });
      return { nodes, edges };
    }

    // NOTE: Leaf nodes will always be adding a new node and never editing an existing node.
    let newNodes = [...nodes, newNode];
    let newEdges = [...edges];

    const isTerminal = isTerminalNode(nodeState);
    if (isTerminal) {
      // Check if the new node is a terminal node, if so, then we need to remove all of the descendant nodes of
      // the new node.

      // Get all descendant nodes of the nodeToDelete.
      const descendantNodes = getDescendantNodes({ nodeId: processingNodeId, nodes: newNodes, edges: newEdges });
      const descendantNodeIds = descendantNodes.map((node) => node.id);

      // Filter out all descendant nodes from the nodes array.
      newNodes = newNodes.filter((node) => !descendantNodeIds.includes(node.id));
      // Filter out all edges that connect any descendant nodes of the new node
      newEdges = newEdges.filter((edge) => !descendantNodeIds.includes(edge.sourceId));
    } else {
      // If not adding a terminal node, then we need to make a connection from the new node to the first child node
      // of the parent node.

      // New connection from the new node to the parent node's old target
      const targetEdge = {
        id: `${newNodeId}->${parentEdge.targetId}`,
        sourceId: newNodeId,
        targetId: parentEdge.targetId,
        type: 'basic',
      };

      newEdges.push(targetEdge);
    }

    // Update the parent node's edge to target the new node.
    const index = edges.findIndex((edge) => edge.sourceId === processingNodeId);
    newEdges[index] = { ...newEdges[index], targetId: newNodeId };

    return sortEdgesDepthFirst({ nodes: newNodes, edges: newEdges });
  }
};

/**
 * Removes a node and its associated edges from the graph. If the node is associative,
 * it also removes all descendant nodes and their edges. If the node is not associative,
 * it updates the edges to maintain the graph structure.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node to delete.
 * @param {Node[]} params.nodes - The array of nodes in the graph.
 * @param {Edge[]} params.edges - The array of edges in the graph.
 * @returns {Object} - An object containing the updated nodes and edges arrays.
 * @returns {Node[]} returns.nodes - The updated array of nodes.
 * @returns {Edge[]} returns.edges - The updated array of edges.
 */
export const getNodesForDelete = ({
  nodeId,
  nodes,
  edges,
}: {
  nodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}) => {
  const nodeToDelete = nodes.find((node) => node.id === nodeId);
  if (!nodeToDelete) {
    logError('Could not find node to delete: PC-l93hd7a', 'getNodesForDelete', {
      nodeId,
      nodeIds: nodes.map((node) => node.id),
    });
    return { nodes, edges };
  }

  const isAssociative = isAssociativeNode(nodeToDelete.type);
  if (isAssociative || nodeToDelete.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS) {
    // Get all descendant nodes of the nodeToDelete.
    const descendantNodes = getDescendantNodes({ nodeId, nodes: nodes, edges: edges });
    const descendantNodeIds = descendantNodes.map((node) => node.id);

    // Filter out the nodeToDelete and all descendant nodes from the nodes array.
    let newNodes = nodes.filter((node) => !descendantNodeIds.includes(node.id) && node.id !== nodeId);
    // Filter out all edges that connect any descendant nodes of the nodeToDelete and also the edge that connects the
    // parent node to the nodeToDelete and the edge that connects the node to delete to it's child node (if it exists).
    const newEdges = edges.filter(
      (edge) => !descendantNodeIds.includes(edge.sourceId) && edge.targetId !== nodeId && edge.sourceId !== nodeId
    );

    if (nodeToDelete.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS) {
      // If the nodeToDelete is a break hours node, then we need to remove the corresponding break schedule from the parent Phone Hour node
      // phoneHours data (from it's call object data).
      const parentNode = newNodes.find((node) => node.id === edges.find((edge) => edge.targetId === nodeId)?.sourceId);

      newNodes = newNodes.map((node) => {
        if (node.id === parentNode?.id && node.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS) {
          return {
            ...node,
            callObject: {
              ...node.callObject,
              phoneHoursExpansion: {
                ...node.callObject.phoneHoursExpansion,
                phoneHours: node.callObject.phoneHoursExpansion?.phoneHours.filter(
                  (hours) =>
                    !(hours.type === ScheduleType.BREAK && hours.scheduleId === nodeToDelete.callObject.primitiveId)
                ),
              },
            },
          };
        }
        return node;
      });
    }

    return sortEdgesDepthFirst({ nodes: newNodes, edges: newEdges });
  } else {
    // The edge that connects the parent node to the nodeToDelete
    const parentEdge = edges.find((edge) => edge.targetId === nodeId);
    // The edge that connects the nodeToDelete to it's child node (if it exists).
    const childEdge = edges.find((edge) => edge.sourceId === nodeId);

    // Filter out the nodeToDelete from the nodes array.
    const newNodes = nodes.filter((node) => node.id !== nodeId);
    // Filter out the edge that point out of the nodeToDelete from the edges array.
    const newEdges = edges.filter((edge) => edge.sourceId !== nodeId);

    // If the parentEdge and childEdge exist, then we need to update the targetId of the parentEdge
    // to target the child node of the nodeToDelete.
    if (parentEdge && childEdge) {
      const index = newEdges.findIndex((edge) => edge.sourceId === parentEdge.sourceId);
      newEdges[index] = {
        ...newEdges[index],
        targetId: childEdge?.targetId ?? '',
      };
    }

    if (!childEdge) {
      // If the nodeToDelete is a leaf node, then we need to remove the edge that connects the parent node to the nodeToDelete.
      const index = newEdges.findIndex((edge) => edge.targetId === nodeId);
      newEdges.splice(index, 1);
    }

    return sortEdgesDepthFirst({ nodes: newNodes, edges: newEdges });
  }
};

/**
 * Helper function that will return the nodes and edges that need to be added to the call route in order to add a phone hours step.
 * Will remove all nodes and edges that are descendants of the node that the user wants to add the phone hours step to and add the
 * corresponding phone hours node along with the "closed", "open" and "break" nodes (and their connecting edges).
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.extendingNodeId - The ID of the node to which a step is being added.
 * @param {Node[]} params.nodes - The current list of nodes.
 * @param {Edge[]} params.edges - The current list of edges.
 * @param {Object} params.data - The data object containing the callObject.
 * @param {NodeStateWithAdditionalData} params.data.callObject - The call object state.
 *
 * @returns {Object} An object containing the updated nodes and edges.
 * @returns {Node[]} returns.nodes - The updated list of nodes.
 * @returns {Edge[]} returns.edges - The updated list of edges.
 */
export const getPhoneHoursNodesForAdd = ({
  extendingNodeId,
  nodes,
  edges,
  nodeState: { type, callObject },
}: {
  extendingNodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  nodeState: NodeStateWithAdditionalDataOfType<CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS>['nodeState'];
}) => {
  if (!type || !callObject) {
    logError('Missing type or callObject: PC-wyd74ik', 'getPhoneHoursNodesForAdd', {
      extendingNodeId,
    });
    return {
      nodes,
      edges,
    };
  }

  // Get the node that the user wants to add a step to.
  const extendingNode = nodes.find((node) => node.id === extendingNodeId);
  if (!extendingNode) {
    logError('Could not find extending node: PC-dik3950', 'getPhoneHoursNodesForAdd', {
      extendingNodeId,
      nodes: nodes.map((n) => n.id),
    });
    return {
      nodes,
      edges,
    };
  }

  const descendants = getDescendantNodes({ nodeId: extendingNodeId ?? '', nodes, edges });
  const descendantNodeIds = descendants.map((node) => node.id);

  // Remove the descendants of the extending node.
  const filteredNodes = nodes.filter((node) => !descendantNodeIds.includes(node.id));
  // Remove the edges that are connected to the descendants of the extending node and also remove the edges that currently connect
  // to the extending node where the extending node is the source.
  const filteredEdges = edges.filter(
    (edge) =>
      edge.sourceId !== extendingNodeId &&
      !descendantNodeIds.includes(edge.sourceId) &&
      !descendantNodeIds.includes(edge.targetId)
  );

  // Create the new phone hours node.
  const newAssociativeNode = {
    id: genUUIDV4(),
    type,
    callObject,
    isPhoneTreeDescendent: false,
  };

  // Create the new edge that connects the new node to the parent node.
  const newEdgeToConnectPhoneTree = {
    id: `${extendingNodeId}:${newAssociativeNode.id}`,
    sourceId: extendingNodeId ?? '',
    targetId: newAssociativeNode.id,
    type: 'basic',
  };

  const phoneHoursExpansionData: PhoneHoursExpansion = callObject?.phoneHoursExpansion;
  // Create the new nodes for every break. Create an edge that connects the new nodes to the new phone hours node.
  const breaks = phoneHoursExpansionData?.phoneHours?.filter((hours) => hours.type === ScheduleType.BREAK) ?? [];
  const { newNodes, newEdges } = breaks.reduce<{ newNodes: CallRouteV1.Types.Node[]; newEdges: Edge[] }>(
    (acc, brk) => {
      const { newNodes, newEdges } = acc;
      const { node, edge } = getNewNodeAndEdge({
        nodeType: CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS,
        parentNodeId: newAssociativeNode.id,
        name: brk.name,
      });

      return {
        newNodes: [...newNodes, node],
        newEdges: [...newEdges, edge],
      };
    },
    { newNodes: [], newEdges: [] }
  );

  // Add one extra "Open" node connected to the phone hours node (Phone hours nodes should always have a
  // connected Open node).
  const { node: newOpenHoursNode, edge: newOpenHoursEdge } = getNewNodeAndEdge({
    nodeType: CallRouteV1.Types.NodeType.NODE_TYPE_OPEN_HOURS,
    parentNodeId: newAssociativeNode.id,
    name: 'Open',
  });

  // Add the new Open Hours node and its connecting edge to the new nodes and edges.
  newNodes.push(newOpenHoursNode);
  newEdges.push(newOpenHoursEdge);

  // Add one extra "Closed" node connected to the phone hours node (Phone hours nodes should always have a
  // connected Closed node).
  const { node: newClosedHoursNode, edge: newClosedHoursEdge } = getNewNodeAndEdge({
    nodeType: CallRouteV1.Types.NodeType.NODE_TYPE_CLOSED_HOURS,
    parentNodeId: newAssociativeNode.id,
    name: 'Closed',
  });

  // Add the new Open Hours node and its connecting edge to the new nodes and edges.
  newNodes.push(newClosedHoursNode);
  newEdges.push(newClosedHoursEdge);

  // Combine the new nodes and edges with the existing nodes and edges. The edges will be added after the index of the edge
  // that has the targetId of the parent node in order for the react-flow to position it in the correct order.
  const combinedNodes = [...filteredNodes, newAssociativeNode, ...newNodes];
  const combinedEdges = [...filteredEdges, ...newEdges, newEdgeToConnectPhoneTree];

  return sortEdgesDepthFirst({ nodes: combinedNodes, edges: combinedEdges });
};

/**
 * Helper function used for getting the updated nodes and edges when editing a phone hours node.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.editingNodeId - The ID of the node being edited.
 * @param {Node[]} params.nodes - The current list of nodes.
 * @param {Edge[]} params.edges - The current list of edges.
 * @param {Object} params.data - The data object containing the callObject.
 * @param {NodeStateWithAdditionalData} params.data.callObject - The call object state.
 *
 * @returns {Object} An object containing the updated nodes and edges.
 * @returns {Node[]} returns.nodes - The updated list of nodes.
 * @returns {Edge[]} returns.edges - The updated list of edges.
 */
export const getPhoneHoursNodesForEdit = ({
  editingNodeId,
  nodes,
  edges,
  nodeState: { type, callObject },
}: {
  editingNodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  nodeState: NodeStateWithAdditionalDataOfType<CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS>['nodeState'];
}) => {
  if (!type || !callObject) {
    logError('Missing type or callObject: PC-8eu3i0w', 'getPhoneHoursNodesForEdit', {
      editingNodeId,
    });
    return {
      nodes,
      edges,
    };
  }

  // Get the node that the user wants to edit
  const editingNode = nodes.find((node) => node.id === editingNodeId);
  if (!editingNode) {
    logError('Could not find editing node: PC-jsnf639', 'getPhoneHoursNodesForEdit', {
      nodes: nodes.map((n) => n.id),
      editingNodeId,
      type,
    });
    return {
      nodes,
      edges,
    };
  }

  const phoneHoursExpansionData: PhoneHoursExpansion = callObject?.phoneHoursExpansion;
  const phoneHourBreaks = phoneHoursExpansionData?.phoneHours
    ?.filter((hours) => hours.type === ScheduleType.BREAK)
    .map((hours) => hours.scheduleId);

  // Get all the current nodes that are direct children of the editing node that are still part of the phone hours.
  const descendantsToKeep = edges
    .filter((edge) => edge.sourceId === editingNodeId)
    .map((edge) =>
      nodes.find(
        (node) =>
          node.id === edge.targetId &&
          (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_CLOSED_HOURS ||
            node.type === CallRouteV1.Types.NodeType.NODE_TYPE_OPEN_HOURS ||
            (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS &&
              phoneHourBreaks.includes(node.callObject.primitiveId)))
      )
    )
    .filter(Boolean);

  // Get all descendants of the editing node that are no longer part of the phone hours.
  const descendantsToRemove = getDescendantNodes({
    nodeId: editingNodeId ?? '',
    nodes,
    edges,
    omitIds: descendantsToKeep.map((node) => node.id),
  });

  const descendantsToRemoveIds = descendantsToRemove.map((node) => node.id);

  // Remove the descendants of the editing node if they are no longer part of the phone hours.
  const filteredNodes = nodes.filter((node) => !descendantsToRemoveIds.includes(node.id));
  // Remove the edges that are connected to the descendants of the editing node that are no longer part of the phone hours.
  const filteredEdges = edges.filter(
    (edge) => !descendantsToRemoveIds.includes(edge.sourceId) && !descendantsToRemoveIds.includes(edge.targetId)
  );

  // The breaks for which there are currently nodes in the call route.
  const currentBreaks = descendantsToKeep
    .filter((node) => node.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS)
    .map((node) => node.callObject.primitiveId);

  // Reconcile the existing break nodes with any new changes that were made to the schedules using the
  // incoming phone hours expansion data.
  const updatedNodes = filteredNodes.map((node) => {
    if (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS) {
      const updatedData = phoneHoursExpansionData?.phoneHours?.find(
        (hours) => hours.scheduleId === node.callObject.primitiveId
      );
      if (updatedData) {
        return {
          ...node,
          callObject: {
            ...node.callObject,
            primitiveName: updatedData.name,
          },
        };
      }
    }

    return node;
  });

  // The breaks that are part of the incoming phone hours expansion data but are not currently nodes in the call route.
  const newBreaks = phoneHoursExpansionData?.phoneHours?.filter(
    (hours) => hours.type === ScheduleType.BREAK && !currentBreaks.includes(hours.scheduleId)
  );

  // Create the new nodes for every new break. Create an edge that connects the new nodes to the new phone hours node.
  const { newNodes, newEdges } = newBreaks.reduce<{ newNodes: CallRouteV1.Types.Node[]; newEdges: Edge[] }>(
    (acc, hours) => {
      const { newNodes, newEdges } = acc;
      const { node, edge } = getNewNodeAndEdge({
        nodeType: CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS,
        parentNodeId: editingNodeId,
        name: hours.name,
        callObject: {
          primitiveId: hours.scheduleId,
        },
      });

      return {
        newNodes: [...newNodes, node],
        newEdges: [...newEdges, edge],
      };
    },
    { newNodes: [], newEdges: [] }
  );

  if (!descendantsToKeep.some((node) => node.type === CallRouteV1.Types.NodeType.NODE_TYPE_OPEN_HOURS)) {
    // Add one extra "Open" node connected to the phone hours node (Phone hours nodes should always have a
    // connected Open node).
    const { node: newOpenHoursNode, edge: newOpenHoursEdge } = getNewNodeAndEdge({
      nodeType: CallRouteV1.Types.NodeType.NODE_TYPE_OPEN_HOURS,
      parentNodeId: editingNodeId,
      name: 'Open',
    });

    // Add the new Open Hours node and its connecting edge to the new nodes and edges.
    newNodes.push(newOpenHoursNode);
    newEdges.push(newOpenHoursEdge);
  }

  if (!descendantsToKeep.some((node) => node.type === CallRouteV1.Types.NodeType.NODE_TYPE_CLOSED_HOURS)) {
    // Add one extra "Closed" node connected to the phone hours node (Phone hours nodes should always have a
    // connected Closed node).
    const { node: newClosedHoursNode, edge: newClosedHoursEdge } = getNewNodeAndEdge({
      nodeType: CallRouteV1.Types.NodeType.NODE_TYPE_CLOSED_HOURS,
      parentNodeId: editingNodeId,
      name: 'Closed',
    });

    // Add the new Open Hours node and its connecting edge to the new nodes and edges.
    newNodes.push(newClosedHoursNode);
    newEdges.push(newClosedHoursEdge);
  }

  // Combine the new nodes and edges with the existing nodes and edges. The edges will be added after the index of the edge
  // that has the targetId of the parent node in order for the react-flow to position it in the correct order.
  const combinedNodes = [...updatedNodes, ...newNodes];

  const nodeIndex = combinedNodes.findIndex((node) => node.id === editingNodeId);
  if (nodeIndex === -1) {
    logError('Could not find editing node: PC-jd72lfv', 'getPhoneHoursNodesForEdit', {
      nodes: combinedNodes.map((n) => n.id),
      editingNodeId,
      type,
    });
  }
  const newNode = {
    id: editingNodeId,
    type,
    callObject,
    isPhoneTreeDescendent: false,
  };
  combinedNodes[nodeIndex] = newNode;

  const combinedEdges = [...filteredEdges, ...newEdges];

  return sortEdgesDepthFirst({ nodes: combinedNodes, edges: combinedEdges });
};

/**
 * Updates the name of a break within a parent hours node's callObject data as well as the corresponding break node.
 *
 * This function locates the break node being edited and its parent hours node, then updates the break's name
 * in both the parent node's `phoneHoursExpansion.phoneHours` array and the break node's `primitiveName`.
 *
 * @param params - The parameters for the function.
 * @param params.processingNodeId - The ID of the break node being edited.
 * @param params.nodes - The list of all nodes in the call route.
 * @param params.edges - The list of all edges in the call route.
 * @param params.newName - The new name to assign to the break.
 * @returns An object containing the updated nodes and edges, sorted depth-first.
 */
export const getNodesForBreakHoursEdit = ({
  processingNodeId,
  nodes,
  edges,
  newName,
}: {
  processingNodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  newName: string;
}) => {
  const editingNode = nodes.find((node) => node.id === processingNodeId);
  if (!editingNode) {
    logError('Could not find editing node: PC-63yrekd9', 'getNodesForBreakHoursEdit', {
      nodes: nodes.map((n) => n.id),
      processingNodeId,
    });
    return {
      nodes,
      edges,
    };
  }

  const parentHoursNodeId = edges.find((edge) => edge.targetId === processingNodeId)?.sourceId;
  if (!parentHoursNodeId) {
    logError('Could not find parent hours node: PC-idk37gx', 'getNodesForBreakHoursEdit', {
      processingNodeId,
      edgeTargetIds: edges.map((edge) => edge.targetId),
    });
    return {
      nodes,
      edges,
    };
  }

  const newNodes = nodes.map((node) => {
    if (node.id === parentHoursNodeId && node.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS) {
      // Update the name of the break inside the parent hours node's callObject.
      return {
        ...node,
        callObject: {
          ...node.callObject,
          phoneHoursExpansion: {
            ...node.callObject.phoneHoursExpansion,
            phoneHours: node.callObject.phoneHoursExpansion?.phoneHours.map((hours) => {
              if (hours.type === ScheduleType.BREAK && hours.scheduleId === editingNode.callObject.primitiveId) {
                return {
                  ...hours,
                  name: newName,
                };
              }
              return hours;
            }),
          },
        },
      };
    } else if (node.id === processingNodeId && node.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS) {
      // Update the name of the break node.
      return {
        ...node,
        callObject: {
          ...node.callObject,
          primitiveName: newName,
        },
      };
    }
    // If the node is not the parent hours node or the break node, return it as is.
    return node;
  });

  return sortEdgesDepthFirst({ nodes: newNodes, edges });
};

/**
 * Helper function that will return the nodes and edges that need to be added to the call route in order to add a phone
 * tree step. Will remove all nodes and edges that are descendants of the node that the user wants to add the phone tree
 * step to and add the corresponding phone tree node along with the dial options and fallback nodes (and their
 * connecting edges).
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.extendingNodeId - The ID of the node to which a step is being added.
 * @param {Node[]} params.nodes - The current list of nodes.
 * @param {Edge[]} params.edges - The current list of edges.
 * @param {NodeStateWithAdditionalData} params.data - The data containing type, callObject, and dialOptions.
 * @param {Object} params.data.callObject - The call object data associated with the new phone tree node.
 * @param {string[]} [params.data.dialOptions=[]] - The dial options for the new phone tree node.
 *
 * @returns {Object} An object containing the updated nodes and edges.
 * @returns {Node[]} returns.nodes - The updated list of nodes.
 * @returns {Edge[]} returns.edges - The updated list of edges.
 */
export const getPhoneTreeNodesForAdd = ({
  extendingNodeId,
  nodes,
  edges,
  nodeState: { type, callObject },
  additionalData: { dialOptions = [] },
}: {
  extendingNodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  nodeState: NodeStateWithAdditionalDataOfType<CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE>['nodeState'];
  additionalData: NodeStateWithAdditionalData['additionalData'];
}) => {
  if (!type || !callObject) {
    logError('Missing type or callObject: PC-1z9x8c', 'getPhoneTreeNodesForAdd', {
      extendingNodeId,
      type,
      callObject,
    });
    return {
      nodes,
      edges,
    };
  }

  // Get the node that the user wants to add a step to.
  const extendingNode = nodes.find((node) => node.id === extendingNodeId);
  if (!extendingNode) {
    logError('Could not find extending node: PC-p0q2w3', 'getPhoneTreeNodesForAdd', {
      extendingNodeId,
      nodes: nodes.map((n) => n.id),
    });
    return {
      nodes,
      edges,
    };
  }

  const descendants = getDescendantNodes({ nodeId: extendingNodeId ?? '', nodes, edges });
  const descendantNodeIds = descendants.map((node) => node.id);

  // Remove the descendants of the extending node.
  const filteredNodes = nodes.filter((node) => !descendantNodeIds.includes(node.id));
  // Remove the edges that are connected to the descendants of the extending node and also remove the edges that currently connect
  // to the extending node where the extending node is the source.
  const filteredEdges = edges.filter(
    (edge) =>
      edge.sourceId !== extendingNodeId &&
      !descendantNodeIds.includes(edge.sourceId) &&
      !descendantNodeIds.includes(edge.targetId)
  );

  const newPhoneTreeNode = {
    id: genUUIDV4(),
    type,
    callObject,
    isPhoneTreeDescendent: false,
  };

  // Create the new edge that connects the new node to the parent node.
  const newEdgeToConnectPhoneTree = {
    id: `${extendingNodeId}:${newPhoneTreeNode.id}`,
    sourceId: extendingNodeId ?? '',
    targetId: newPhoneTreeNode.id,
    type: 'basic',
  };

  // Create new nodes for every dial option. Create an edge that connects the new node to the new phone tree node.
  const { newNodes, newEdges } = dialOptions.reduce<{ newNodes: CallRouteV1.Types.Node[]; newEdges: Edge[] }>(
    (acc, dialOption) => {
      const { newNodes, newEdges } = acc;
      const { node, edge } = getNewNodeAndEdge({
        nodeType: CallRouteV1.Types.NodeType.NODE_TYPE_TREE_OPTION,
        parentNodeId: newPhoneTreeNode.id,
        name: dialOption,
        isPhoneTreeDescendent: true,
      });

      return {
        newNodes: [...newNodes, node],
        newEdges: [...newEdges, edge],
      };
    },
    { newNodes: [], newEdges: [] }
  );

  // Add one extra "Fallback" node connected to the phone tree (Phone tree nodes should always have a
  // connected fallback node).
  const { node: newFallbackNode, edge: newFallbackEdge } = getNewNodeAndEdge({
    nodeType: CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK,
    parentNodeId: newPhoneTreeNode.id,
    name: 'Fallback',
    isPhoneTreeDescendent: true,
  });

  // Add the new fallback node its connecting edge to the new nodes and edges.
  newNodes.push(newFallbackNode);
  newEdges.push(newFallbackEdge);

  // Combine the new nodes and edges with the existing nodes and edges. The edges will be added after the index of the edge
  // that has the targetId of the parent node in order for the react-flow to position it in the correct order.
  const combinedNodes = [...filteredNodes, newPhoneTreeNode, ...newNodes];
  const combinedEdges = [...filteredEdges, ...newEdges, newEdgeToConnectPhoneTree];

  return sortEdgesDepthFirst({ nodes: combinedNodes, edges: combinedEdges });
};

/**
 * Helper function used for getting the updated nodes and edges when editing a phone tree node.
 *
 * @param params - The parameters for updating the phone tree nodes.
 * @param params.editingNodeId - The ID of the node being edited.
 * @param params.nodes - The current list of nodes in the phone tree.
 * @param params.edges - The current list of edges in the phone tree.
 * @param params.nodeState - The state of the node being edited, including its type and call object.
 * @param params.nodeState.type - The type of the node being edited.
 * @param params.nodeState.callObject - The call object associated with the node being edited.
 * @param params.additionalData - Additional data for the node being edited.
 * @param params.additionalData.dialOptions - The list of dial options to be used for updating the phone tree.
 * @returns The updated nodes and edges for the phone tree, sorted in depth-first order.
 */
export const getPhoneTreeNodesForEdit = ({
  editingNodeId,
  nodes,
  edges,
  nodeState: { type, callObject },
  additionalData: { dialOptions = [] },
}: {
  editingNodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  nodeState: NodeStateWithAdditionalDataOfType<CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE>['nodeState'];
  additionalData: NodeStateWithAdditionalData['additionalData'];
}) => {
  if (!type || !callObject) {
    logError('Missing type or callObject: PC-830ksle3', 'getPhoneTreeNodesForEdit', {
      editingNodeId,
    });
    return {
      nodes,
      edges,
    };
  }

  // Get the node that the user wants to edit.
  const editingNode = nodes.find((node) => node.id === editingNodeId);
  if (!editingNode) {
    logError('Could not find editing node: PC-jd7204o', 'getPhoneTreeNodesForEdit', {
      editingNodeId,
      nodes: nodes.map((n) => n.id),
    });
    return {
      nodes,
      edges,
    };
  }

  // Get all the current nodes that are direct children of the editing node who are still part of the dialOptions.
  const descendantsToKeep = edges
    .filter((edge) => edge.sourceId === editingNodeId)
    .map((edge) =>
      nodes.find(
        (node) =>
          node.id === edge.targetId &&
          (dialOptions?.includes(node.callObject.primitiveName) || node.callObject.primitiveName === 'Fallback')
      )
    )
    .filter(Boolean);

  // Get all descendants of the editing node that are no longer part of the dialOptions.
  const descendantsToRemove = getDescendantNodes({
    nodeId: editingNodeId ?? '',
    nodes,
    edges,
    omitIds: descendantsToKeep.map((node) => node.id),
  });

  // Remove the descendants of the editing node if they are no longer part of the dialOptions.
  const filteredNodes = nodes.filter(
    (node) =>
      !descendantsToRemove.some(
        (descendant) => descendant.id === node.id && !dialOptions?.includes(descendant.callObject.primitiveName)
      )
  );

  // Remove the edges that are connected to the descendants of the editing node that are no longer part of the dialOptions.
  const filteredEdges = edges.filter(
    (edge) =>
      !descendantsToRemove.some((descendant) => edge.sourceId === descendant.id || edge.targetId === descendant.id)
  );

  const newDialOptions = dialOptions.filter(
    (dialOption) => !descendantsToKeep.some((node) => node.callObject.primitiveName === dialOption)
  );

  // Create new nodes for every new dial option. Create an edge that connects the new node to the new phone tree node.
  const { newNodes, newEdges } = newDialOptions.reduce<{ newNodes: CallRouteV1.Types.Node[]; newEdges: Edge[] }>(
    (acc, dialOption) => {
      const { newNodes, newEdges } = acc;
      const { node, edge } = getNewNodeAndEdge({
        nodeType: CallRouteV1.Types.NodeType.NODE_TYPE_TREE_OPTION,
        parentNodeId: editingNode.id,
        name: dialOption,
        isPhoneTreeDescendent: true,
      });

      return {
        newNodes: [...newNodes, node],
        newEdges: [...newEdges, edge],
      };
    },
    { newNodes: [], newEdges: [] }
  );

  if (!descendantsToKeep.some((node) => node.callObject.primitiveName === 'Fallback')) {
    // Add one extra "Fallback" node connected to the phone tree (Phone tree nodes should always have a
    // connected fallback node).
    const { node: newFallbackNode, edge: newFallbackEdge } = getNewNodeAndEdge({
      nodeType: CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK,
      parentNodeId: editingNode.id,
      name: 'Fallback',
      isPhoneTreeDescendent: true,
    });

    // Add the new fallback node its connecting edge to the new nodes and edges.
    newNodes.push(newFallbackNode);
    newEdges.push(newFallbackEdge);
  }

  // Combine the new nodes and edges with the existing nodes and edges. The edges will be added after the index of the edge
  // that has the targetId of the parent node in order for the react-flow to position it in the correct order.
  const combinedNodes = [...filteredNodes, ...newNodes];

  const nodeIndex = combinedNodes.findIndex((node) => node.id === editingNodeId);
  if (nodeIndex === -1) {
    logError('Could not find editing node: PC-kdif742m', 'getPhoneTreeNodesForEdit', {
      nodes: combinedNodes.map((n) => n.id),
      editingNodeId,
      type,
    });
  }
  const newNode = {
    id: editingNodeId,
    type,
    callObject,
    isPhoneTreeDescendent: false,
  };
  combinedNodes[nodeIndex] = newNode;

  // Update any Repeat nodes that have their repeatExpansion.phoneTreeNodeId equal to the editing node ID.
  const updatedNodes = combinedNodes.map((node) => {
    if (
      node.type === CallRouteV1.Types.NodeType.NODE_TYPE_REPEAT &&
      node.callObject.repeatExpansion?.phoneTreeNodeId === editingNodeId
    ) {
      return {
        ...node,
        callObject: {
          ...node.callObject,
          primitiveName: getRepeatPhoneTreeName(newNode),
        },
      };
    }
    return node;
  });

  const combinedEdges = [...filteredEdges, ...newEdges];

  return sortEdgesDepthFirst({ nodes: updatedNodes, edges: combinedEdges });
};
